import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import React, { FC, useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import {
  getGetOrganizationArchivedDocumentsQueryKey,
  getOrganizationArchivedDocuments,
} from "~/api/documents/documents";
import { EmptySpace } from "~/components/EmptySpace";
import { useOrganizationParams } from "~/helpers";
import { ArchivedDocumentsListHeader } from "./ArchivedDocumentsListHeader";
import { ArchivedDocumentsListItem } from "./ArchivedDocumentsListItem";

const ITEMS_PER_PAGE = 20;

export const ArchivedDocumentsList: FC = () => {
  const { formatMessage } = useIntl();
  const { organizationId } = useOrganizationParams();
  const observerTarget = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: getGetOrganizationArchivedDocumentsQueryKey(organizationId),
      queryFn: async ({ pageParam = 1 }) => {
        const response = await getOrganizationArchivedDocuments(
          organizationId,
          {
            page: pageParam as number,
            limit: ITEMS_PER_PAGE,
          }
        );
        return response;
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage?.pagination) return undefined;
        if (lastPage.pagination.currentPage < lastPage.pagination.totalPages) {
          return lastPage.pagination.currentPage + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
    });

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const option = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(handleObserver, option);
    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  if (isLoading) {
    return null;
  }

  if (!data?.pages[0]?.documents.length) {
    return (
      <EmptySpace
        title={formatMessage({
          id: "archivedDocuments.empty",
          defaultMessage: "No documents archived yet",
        })}
      />
    );
  }

  return (
    <div>
      <ArchivedDocumentsListHeader />
      {data.pages.map((page, i) => (
        <React.Fragment key={i}>
          {page?.documents?.map((item) => (
            <ArchivedDocumentsListItem key={item.id} document={item} />
          ))}
        </React.Fragment>
      ))}
      <div ref={observerTarget} className="h-10" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
        </div>
      )}
    </div>
  );
};
